import React, { useState, useEffect, useContext, useRef } from "react";
import "emoji-mart/css/emoji-mart.css";
import { useParams } from "react-router-dom";
import { Picker } from "emoji-mart";
import { Smile, Paperclip, Send, Mic, XCircle, CheckCircle, X, Loader2, ShieldAlert, UserCheck, ClipboardList, Clock } from "lucide-react";
import { toast } from "react-toastify";
import WhatsAppCloudTemplateModal from "../WhatsAppCloudTemplateModal";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import RecordingTimer from "./RecordingTimer";
import { ReplyMessageContext } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import toastError from "../../errors/toastError";
import { usePendingMessages } from "../../context/PendingMessages/PendingMessagesContext";

let mediaRecorderInstance = null;
let audioChunks = [];

const MessageInput = ({ ticketStatus, ticket }) => {
  const { ticketId } = useParams();
  const [medias, setMedias] = useState([]);
  const [inputMessage, setInputMessage] = useState("");
  const [showEmoji, setShowEmoji] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [quickAnswers, setQuickAnswer] = useState([]);
  const [typeBar, setTypeBar] = useState(false);
  const inputRef = useRef();
  const emojiRef = useRef();
  const lastPresenceSent = useRef(0);
  const { setReplyingMessage, replyingMessage } = useContext(ReplyMessageContext);
  const { user } = useContext(AuthContext);
  const [signMessage, setSignMessage] = useLocalStorage("signOption", true);
  const { addPendingMessage, removePendingById } = usePendingMessages();
  const [reassigning, setReassigning] = useState(false);
  const [isNote, setIsNote] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [waWindowOpen, setWaWindowOpen] = useState(true);

  const isCloudChannel =
    ticket?.whatsapp?.channel === "whatsapp_cloud" ||
    ticket?.whatsapp?.channel === "whatsapp_coexistence";

  // Meta's 24h customer-service window: outside it, only templates can be
  // sent on Cloud API channels. Re-checked whenever a new message lands on
  // the ticket (lastMessage changes when the customer replies, reopening it).
  useEffect(() => {
    if (!isCloudChannel || !ticket?.id) {
      setWaWindowOpen(true);
      return;
    }
    api.get(`/api/whatsapp-cloud/window/${ticket.id}`)
      .then(({ data }) => setWaWindowOpen(data.windowOpen !== false))
      .catch(() => setWaWindowOpen(true));
  }, [isCloudChannel, ticket?.id, ticket?.lastMessage]);

  const handleReassign = async () => {
    setReassigning(true);
    try {
      await api.put(`/tickets/${ticket.id}`, {
        status: "open",
        userId: user?.id,
      });
    } catch (err) {
      toastError(err);
    } finally {
      setReassigning(false);
    }
  };

  useEffect(() => {
    const draft = localStorage.getItem(`draft_${ticketId}`);
    setInputMessage(draft || "");
    inputRef.current?.focus();
    return () => {
      setShowEmoji(false);
      setMedias([]);
      setReplyingMessage(null);
    };
  }, [ticketId, setReplyingMessage]);

  useEffect(() => { inputRef.current?.focus(); }, [replyingMessage]);

  useEffect(() => {
    if (!showEmoji) return;
    const handler = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) setShowEmoji(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showEmoji]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  const handleChangeInput = (e) => {
    const value = e.target.value;
    setInputMessage(value);
    if (ticketId) localStorage.setItem(`draft_${ticketId}`, value);
    handleLoadQuickAnswer(value);

    // Send real-time typing status over WhatsApp (throttled to once every 4 seconds)
    const now = Date.now();
    if (now - lastPresenceSent.current > 4000) {
      lastPresenceSent.current = now;
      api.post(`/tickets/${ticketId}/presence`, { presence: "composing" }).catch(() => {});
      // Avisar a OTROS agentes que estoy respondiendo esta conversación.
      try {
        openSocket().emit("agentTyping", { ticketId });
      } catch (e) {
        /* noop: socket opcional */
      }
    }
  };

  const handleQuickAnswersClick = (value) => {
    setInputMessage(value);
    if (ticketId) localStorage.setItem(`draft_${ticketId}`, value);
    setTypeBar(false);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const handleAddEmoji = (e) => {
    setInputMessage((prev) => {
      const newValue = prev + e.native;
      if (ticketId) localStorage.setItem(`draft_${ticketId}`, newValue);
      return newValue;
    });
  };

  const handleChangeMedias = (e) => {
    if (!e.target.files) return;
    setMedias(Array.from(e.target.files));
  };

  const handleInputPaste = (e) => {
    if (e.clipboardData.files[0]) setMedias([e.clipboardData.files[0]]);
  };

  const handleUploadMedia = async (e) => {
    setLoading(true);
    e.preventDefault();
    const formData = new FormData();
    formData.append("fromMe", true);
    medias.forEach((media) => { formData.append("medias", media); formData.append("body", media.name); });
    try { await api.post(`/messages/${ticketId}`, formData); } catch (err) { toastError(err); }
    setLoading(false);
    setMedias([]);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleSendMessage = async () => {
    if (inputMessage.trim() === "") return;
    setLoading(true);
    const body = signMessage ? `*${user?.name}:*\n${inputMessage.trim()}` : inputMessage.trim();
    const message = { read: 1, fromMe: true, mediaUrl: "", body, quotedMsg: replyingMessage, isNote };
    setInputMessage("");
    setShowEmoji(false);
    setReplyingMessage(null);
    const pendingId = !isNote ? addPendingMessage(body, ticketId) : null;
    try {
      await api.post(`/messages/${ticketId}`, message);
      localStorage.removeItem(`draft_${ticketId}`);
      if (isNote) {
        setIsNote(false);
      }
    } catch (err) {
      if (pendingId) removePendingById(pendingId);
      setInputMessage(inputMessage);
      if (err?.response?.data?.error === "ERR_WACLOUD_WINDOW_EXPIRED") {
        setWaWindowOpen(false);
        toast.warn("La ventana de 24 horas expiró. Solo puedes reabrir la conversación enviando una plantilla.");
        setTemplateModalOpen(true);
      } else {
        toastError(err);
      }
    }
    setLoading(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const handleStartRecording = async () => {
    setLoading(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : MediaRecorder.isTypeSupported("audio/ogg;codecs=opus")
        ? "audio/ogg;codecs=opus"
        : "audio/webm";
      audioChunks = [];
      mediaRecorderInstance = new MediaRecorder(stream, { mimeType });
      mediaRecorderInstance.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunks.push(e.data);
      };
      mediaRecorderInstance.start();
      setRecording(true);
      setLoading(false);
    } catch (err) { toastError(err); setLoading(false); }
  };

  const handleLoadQuickAnswer = async (value) => {
    if (value && value.indexOf("/") === 0) {
      try {
        const { data } = await api.get("/quickAnswers/", { params: { searchParam: inputMessage.substring(1) } });
        setQuickAnswer(data.quickAnswers);
        setTypeBar(data.quickAnswers.length > 0);
      } catch { setTypeBar(false); }
    } else { setTypeBar(false); }
  };

  const handleUploadAudio = async () => {
    setLoading(true);
    try {
      if (!mediaRecorderInstance || mediaRecorderInstance.state === "inactive") {
        throw new Error("No active recording");
      }
      const blob = await new Promise((resolve) => {
        mediaRecorderInstance.onstop = () => {
          resolve(new Blob(audioChunks, { type: mediaRecorderInstance.mimeType }));
        };
        mediaRecorderInstance.stop();
        mediaRecorderInstance.stream.getTracks().forEach((t) => t.stop());
      });
      if (blob.size < 1000) {
        toastError(i18n.t("messagesInput.audioTooShort") || "Recording too short, please try again.");
        setLoading(false);
        setRecording(false);
        return;
      }
      const ext = mediaRecorderInstance.mimeType.includes("ogg") ? "ogg" : "webm";
      const filename = `${Date.now()}.${ext}`;
      const formData = new FormData();
      formData.append("medias", blob, filename);
      formData.append("body", filename);
      formData.append("fromMe", "true");
      await api.post(`/messages/${ticketId}`, formData);
    } catch (err) { toastError(err); }
    mediaRecorderInstance = null;
    audioChunks = [];
    setRecording(false);
    setLoading(false);
  };

  const handleCancelAudio = () => {
    try {
      if (mediaRecorderInstance && mediaRecorderInstance.state !== "inactive") {
        mediaRecorderInstance.stop();
        mediaRecorderInstance.stream.getTracks().forEach((t) => t.stop());
      }
    } catch (err) { /* ignore */ }
    mediaRecorderInstance = null;
    audioChunks = [];
    setRecording(false);
  };

  const isAssignedToOther = ticket?.status === "open" && ticket?.userId && ticket?.userId !== user?.id;

  if (isAssignedToOther) {
    return (
      <div className="flex flex-col items-center justify-center p-6 border-t border-slate-200 dark:border-slate-800 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/10 dark:to-orange-950/10 transition-all duration-300">
        <div className="flex flex-col sm:flex-row items-center gap-4 text-center sm:text-left max-w-2xl w-full">
          <div className="p-3 bg-amber-100 dark:bg-amber-900/40 rounded-2xl text-amber-600 dark:text-amber-400 animate-pulse flex-shrink-0">
            <ShieldAlert className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-slate-800 dark:text-slate-200">
              {i18n.t("messagesInput.assignedToOtherTitle")}
            </h4>
            <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
              {i18n.t("messagesInput.assignedToOtherDesc") + (ticket?.user?.name || "otro agente") + i18n.t("messagesInput.assignedToOtherDescEnd")}
            </p>
          </div>
          <button
            onClick={handleReassign}
            disabled={reassigning}
            className="flex items-center gap-2 px-4 py-2.5 text-xs font-bold text-white bg-indigo-600 dark:bg-indigo-500 rounded-xl hover:bg-indigo-700 dark:hover:bg-indigo-600 active:scale-95 transform transition-all shadow-md shadow-indigo-200 dark:shadow-none disabled:opacity-50 flex-shrink-0"
          >
            {reassigning ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UserCheck className="w-4 h-4" />
            )}
            {i18n.t("messagesInput.reassignToMe")}
          </button>
        </div>
      </div>
    );
  }

  const disabled = loading || recording || ticketStatus !== "open";

  if (medias.length > 0) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        <button onClick={() => setMedias([])} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
          <X className="w-5 h-5" />
        </button>
        <span className="flex-1 text-sm text-slate-600 dark:text-slate-300 truncate">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : medias[0]?.name}
        </span>
        <button onClick={handleUploadMedia} disabled={loading} className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-950/30 transition-colors">
          <Send className="w-5 h-5" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
      {/* 24h window expired banner (WhatsApp Cloud) */}
      {isCloudChannel && !waWindowOpen && ticketStatus === "open" && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-amber-50 dark:bg-amber-950/20 border-b border-amber-200 dark:border-amber-900/40">
          <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0" />
          <p className="flex-1 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
            La ventana de 24 horas está cerrada. Meta solo permite reabrir la conversación con una <strong>plantilla aprobada</strong>; volverá a abrirse cuando el cliente responda.
          </p>
          <button
            type="button"
            onClick={() => setTemplateModalOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg transition-colors flex-shrink-0"
          >
            <ClipboardList className="w-3.5 h-3.5" />
            Enviar plantilla
          </button>
        </div>
      )}

      {/* Reply preview */}
      {replyingMessage && (
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <span className="w-1 h-full rounded bg-emerald-500 self-stretch" />
          <div className="flex-1 min-w-0">
            {!replyingMessage.fromMe && (
              <p className="text-xs font-semibold text-sky-500">{replyingMessage.contact?.name}</p>
            )}
            <p className="text-xs text-slate-600 dark:text-slate-400 truncate">{replyingMessage.body}</p>
          </div>
          <button
            onClick={() => setReplyingMessage(null)}
            disabled={loading || ticketStatus !== "open"}
            className="p-1 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="flex items-end gap-2 px-3 py-2">
        {/* Emoji */}
        <div className="relative" ref={emojiRef}>
          <button
            onClick={() => setShowEmoji((p) => !p)}
            disabled={disabled}
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-40"
          >
            <Smile className="w-5 h-5" />
          </button>
          {showEmoji && (
            <div className="absolute bottom-12 left-0 z-50">
              <Picker perLine={16} showPreview={false} showSkinTones={false} onSelect={handleAddEmoji} />
            </div>
          )}
        </div>

        {/* File upload */}
        <label htmlFor="upload-button-input" className={`p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
          <Paperclip className="w-5 h-5" />
        </label>
        <input id="upload-button-input" multiple type="file" className="hidden" disabled={disabled} onChange={handleChangeMedias} />

        {/* WhatsApp Cloud Template Sender */}
        {(ticket.whatsapp?.channel === "whatsapp_cloud" || ticket.whatsapp?.channel === "whatsapp_coexistence") && (
          <button
            type="button"
            onClick={() => setTemplateModalOpen(true)}
            disabled={disabled}
            className="p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 flex-shrink-0"
            title="Enviar Plantilla"
          >
            <ClipboardList className="w-5 h-5" />
          </button>
        )}

        {/* Input */}
        <div className="relative flex-1 flex items-center">
          <textarea
            ref={inputRef}
            className={`w-full pl-4 pr-20 py-2.5 text-sm rounded-2xl resize-none placeholder-slate-400 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none transition-all ${
              isNote
                ? "bg-amber-50/70 dark:bg-amber-950/20 border border-amber-300 dark:border-amber-900/50"
                : "bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700"
            }`}
            rows={1}
            style={{ maxHeight: "240px", overflowY: "hidden" }}
            placeholder={isNote ? "Escribe una nota interna..." : ticketStatus === "open" ? i18n.t("messagesInput.placeholderOpen") : i18n.t("messagesInput.placeholderClosed")}
            value={inputMessage}
            onChange={handleChangeInput}
            disabled={recording || loading || ticketStatus !== "open"}
            onPaste={(e) => { if (ticketStatus === "open") handleInputPaste(e); }}
            onKeyDown={(e) => { if (!loading && !e.shiftKey && e.key === "Enter") { e.preventDefault(); handleSendMessage(); } }}
          />
          {ticketStatus === "open" && (
            <button
              type="button"
              onClick={() => setIsNote((p) => !p)}
              className={`absolute right-2 top-1.5 px-2.5 py-1 text-[10px] font-bold rounded-lg transition-all duration-300 shadow-sm border ${
                isNote
                  ? "bg-amber-500 text-white border-amber-400 hover:bg-amber-600"
                  : "bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:bg-slate-200 dark:hover:bg-slate-600"
              }`}
            >
              📌 {isNote ? "Nota Interna" : "Nota"}
            </button>
          )}
          {typeBar && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg overflow-hidden z-50">
              {quickAnswers.map((value, index) => (
                <button
                  key={index}
                  onClick={() => handleQuickAnswersClick(value.message)}
                  className="w-full text-left px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors truncate"
                >
                  <span className="font-semibold text-indigo-600">/{value.shortcut}</span> — {value.message}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sign toggle */}
        <label className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 cursor-pointer flex-shrink-0">
          <div
            onClick={() => setSignMessage((p) => !p)}
            className={`relative w-7 h-4 rounded-full transition-colors ${signMessage ? "bg-indigo-600" : "bg-slate-300 dark:bg-slate-600"}`}
          >
            <span className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${signMessage ? "translate-x-3.5" : "translate-x-0.5"}`} />
          </div>
          <span className="hidden sm:block">{i18n.t("messagesInput.signMessage")}</span>
        </label>

        {/* Send / Mic / Recording */}
        {inputMessage ? (
          <button onClick={handleSendMessage} disabled={loading} className="p-2 rounded-full bg-indigo-600 text-white hover:bg-indigo-700 transition-colors disabled:opacity-50 flex-shrink-0">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        ) : recording ? (
          <div className="flex items-center gap-1 flex-shrink-0">
            <button onClick={handleCancelAudio} disabled={loading} className="p-2 rounded-full text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 transition-colors">
              <XCircle className="w-5 h-5" />
            </button>
            {loading ? <Loader2 className="w-4 h-4 animate-spin text-slate-400" /> : <RecordingTimer />}
            <button onClick={handleUploadAudio} disabled={loading} className="p-2 rounded-full text-emerald-500 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 transition-colors">
              <CheckCircle className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <button onClick={handleStartRecording} disabled={loading || ticketStatus !== "open"} className="p-2 rounded-full text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors disabled:opacity-40 flex-shrink-0">
            <Mic className="w-5 h-5" />
          </button>
        )}
      </div>
      <WhatsAppCloudTemplateModal
        open={templateModalOpen}
        onClose={() => setTemplateModalOpen(false)}
        ticket={ticket}
      />
    </div>
  );
};

export default MessageInput;
